import { useEffect, useMemo } from 'react';

import groupBy from 'lodash/groupBy';
import { useMatch, useParams } from 'react-router-dom';

import {
    type CombinedCollectionMetadata,
    type CombinedCollectionMetadataReturn,
    getAptosCollectionMetadata,
    getContinuationPagesData,
    type UseDaasGetCollectionMetadataProps,
    useGetActiveListingCounts,
    useGetCollectionOwners,
    useGetCollectionTraits,
    useGetMultichainCollectionTokenCount,
    useGetSingleCollectionMetadata,
    useQueryDisabled,
} from '@pkg/hooks';

import {
    type AptosCollectionMetadata,
    CollectionChainParam,
    type CollectionTrait,
    SupportedTokens,
    type TokenCount,
} from '@app/client/common/structs';

export function useCombinedMultichainCollectionMetadata(args: UseDaasGetCollectionMetadataProps) {
    const collectionMatch = Boolean(useMatch({ path: 'collection', end: false }));
    const { slug, disabled, chain, includeAttributes } = args;
    const { chainParam, collectionSlugParam } = useParams();
    const querySlug = slug || collectionSlugParam;
    let queryChain = '';
    if (chain) {
        queryChain = chain;
    } else if (chainParam) {
        queryChain = SupportedTokens[CollectionChainParam[chainParam]];
    }
    const querySlugString = `${queryChain}.${querySlug}`;
    const queryDisabled = useQueryDisabled([!disabled, queryChain, querySlug]);

    const { data, ...collectionRest } = useGetSingleCollectionMetadata({
        ...args,
        slug: querySlug,
        includeAttributes,
        disabled: queryDisabled,
        chain: queryChain as SupportedTokens,
        ignoreCancel: true,
    });

    const collectionData =
        queryChain === SupportedTokens.Aptos
            ? data && getAptosCollectionMetadata(data as unknown as AptosCollectionMetadata)
            : data;

    const {
        data: traitData,
        isLoading: traitsLoading,
        isFetching: traitsFetching,
        hasNextPage,
        fetchNextPage,
        isFetched,
    } = useGetCollectionTraits({ disabled: !collectionMatch });

    const shouldFetchMoreTraits = useMemo(
        () => Boolean(!!traitData && isFetched && hasNextPage && !traitsFetching && !traitsLoading),
        [hasNextPage, isFetched, traitData, traitsFetching, traitsLoading],
    );

    useEffect(() => {
        if (shouldFetchMoreTraits) {
            void fetchNextPage();
        }
    }, [fetchNextPage, shouldFetchMoreTraits]);

    const traits = useMemo(() => getContinuationPagesData(traitData, 'traits'), [traitData]);

    const groupedTraits = useMemo(
        () =>
            groupBy(
                traits?.filter((t: CollectionTrait) => !!t && t?.value !== 'None'),
                'name',
            ) as unknown as CombinedCollectionMetadata['traits'],
        [traits],
    );
    const {
        data: tokenCount,
        isLoading: tokenIsLoading,
        isFetching: tokenIsFetching,
    } = useGetMultichainCollectionTokenCount({
        ...args,
        slugs: [querySlugString],
        disabled: queryDisabled,
    });

    const { countData } = useGetCollectionOwners({ slugQuery: querySlugString, countOnly: true });

    const { data: activeListingCount } = useGetActiveListingCounts({
        slugQuery: querySlugString,
        chain: queryChain as SupportedTokens,
        count_only: true,
    });

    const tokenSupply = queryChain
        ? tokenCount?.[queryChain]?.find((tc: TokenCount) => tc.slug === querySlug)?.count
        : undefined;

    const percentageListed = useMemo(() => {
        const count = activeListingCount?.counts?.find((count) => count.slug === querySlug)?.count;
        if (count && tokenSupply > 0) {
            const percentage = (count / tokenSupply) * 100;
            return percentage >= 100 ? 100 : percentage;
        }
    }, [activeListingCount?.counts, querySlug, tokenSupply]);

    const combinedMetadata: CombinedCollectionMetadata = {
        ...(collectionData || {}),
        tokenCount: tokenSupply,
        ownerCount: countData?.count,
        percentageListed,
        chain: queryChain,
        traits: groupedTraits,
    };

    const metadata: CombinedCollectionMetadataReturn = {
        ...collectionRest,
        data: combinedMetadata,
        isLoading: tokenIsLoading || collectionRest.isLoading || traitsLoading,
        isFetching: tokenIsFetching || collectionRest.isFetching || traitsFetching,
    };

    return metadata;
}
